/**
 * Single source of truth for company facts, navigation and SEO defaults.
 *
 * Every value in `company` is taken verbatim from the Impressum of the old
 * site (scraped-site/pages/impressum.md). Nothing here is invented - if a
 * fact is not in the source material it is not stated on the site.
 */

/*
 * The canonical origin. Everything SEO-facing derives from this constant -
 * canonicals, og:url, the sitemap, robots.txt and the JSON-LD - so the site is
 * only ever indexed under one hostname.
 *
 * That hostname is `.de`, the domain the business is known by. `.com` is owned
 * too and 301s here at the edge (see _nginx/casa-hausverwaltung.de); it must
 * never serve a copy of the site, because two hostnames with identical pages
 * split the ranking signals between them.
 */
export const SITE_URL = 'https://casa-hausverwaltung.de'

export const company = {
  brand: 'CASA Hausverwaltung',
  shortBrand: 'CASA',
  legalName: 'B+K Immo Invest GmbH',
  tagline: 'Hausverwaltung besser gemacht',
  street: 'Nagelsee 2',
  postalCode: '78554',
  city: 'Aldingen',
  country: 'Deutschland',
  countryCode: 'DE',
  phone: '+49 7424 6053100',
  phoneHref: 'tel:+4974246053100',
  email: 'info@casa-hausverwaltung.de',
  emailHref: 'mailto:info@casa-hausverwaltung.de',
  managingDirectors: ['Oğuz Budak', 'Emre Karadağ'],
  licence: 'Zugelassene Hausverwalter nach § 34c GewO',
  vatId: 'DE357281003',
  registration: 'HRB 778984',
  registerCourt: 'Amtsgericht Stuttgart',
}

/** The tenant / owner portal the old site linked to. */
export const app = {
  name: 'CASA App',
  partner: 'iDWELL',
  partnerUrl: 'https://www.idwell.de/',
  portalUrl: 'https://casa.idwell.com/',
  loginUrl:
    'https://files.idwell.com/external-login-form/public/index.html?host=casa.idwell.com&background=true',
  android:
    'https://play.google.com/store/apps/details?id=com.idwellmobile&hl=de_AT',
  ios: 'https://apps.apple.com/at/app/idwell/id1235066877',
}

/**
 * The Pipedrive web form embedded on /kontakt.
 *
 * Enquiries go straight into Pipedrive; the fields, the copy inside the form and
 * its styling are configured over there, not here. Replacing the form in
 * Pipedrive means replacing this URL - the id in it identifies one specific form.
 *
 * Note that this is the site's only third-party embed: it loads a script and an
 * iframe from webforms.pipedrive.com and sets cookies, which is why the site
 * needs a consent banner. See src/components/PipedriveForm.jsx.
 */
export const pipedriveForm = {
  url: 'https://webforms.pipedrive.com/f/czPSJBkGbPWwyxHkiiIaGnws7O19xz5zNGv711lCUNVKSSeg5ebGrU65SJSL491TQT',
}

/** Primary navigation. `Kontakt` lives in the header as a button instead. */
export const nav = [
  { label: 'Verwalten', to: '/verwalten' },
  { label: 'Leistungen', to: '/leistungen' },
  { label: 'CASA App', to: '/casa-app' },
  { label: 'Insights', to: '/insights' },
  { label: 'Über uns', to: '/ueber-uns' },
]

export const footerNav = {
  leistungen: [
    { label: 'WEG-Verwaltung', to: '/verwalten#weg-verwaltung' },
    { label: 'Sondereigentumsverwaltung', to: '/verwalten#sondereigentum' },
    { label: 'Mietverwaltung', to: '/verwalten#mietverwaltung' },
    { label: 'Pakete & Preise', to: '/leistungen' },
  ],
  unternehmen: [
    { label: 'Über uns', to: '/ueber-uns' },
    { label: 'CASA App', to: '/casa-app' },
    { label: 'Insights', to: '/insights' },
    { label: 'Kontakt', to: '/kontakt' },
  ],
  legal: [
    { label: 'Impressum', to: '/impressum' },
    { label: 'Datenschutzerklärung', to: '/datenschutz' },
  ],
}

/** Fallback metadata; every route overrides title and description. */
export const seoDefaults = {
  titleTemplate: (title) => `${title} - ${company.brand}`,
  homeTitle: `${company.brand} - ${company.tagline}`,
  description:
    'CASA Hausverwaltung übernimmt WEG-Verwaltung, Sondereigentums- und Mietverwaltung in der Region Aldingen. Regional, digital und mit Rückmeldung innerhalb eines Werktages.',
  locale: 'de_DE',
  language: 'de-DE',

  /*
   * The social card. Absolute URLs are required - Facebook, LinkedIn and X all
   * reject a relative og:image - so `src/lib/seo.js` prefixes SITE_URL.
   * Dimensions are declared alongside it so a scraper can lay the card out
   * before it has finished downloading the file.
   */
  ogImage: '/images/og-default.jpg',
  ogImageWidth: '1200',
  ogImageHeight: '630',
  ogImageAlt:
    'CASA Hausverwaltung - Hausverwaltung besser gemacht. WEG-Verwaltung, Sondereigentum, Mietverwaltung in 78554 Aldingen.',

  /*
   * Indexing directives. `max-image-preview:large` is what allows Google to
   * show the big thumbnail in a mobile result, and `max-snippet:-1` lifts the
   * snippet-length cap; without them Google applies the conservative defaults.
   */
  robots: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
}

/**
 * Human labels for each route, used to build breadcrumb trails.
 *
 * Breadcrumbs are derived from the URL rather than declared per page so a new
 * route cannot silently ship without one. Article slugs are not listed - their
 * title comes from the post itself.
 */
export const routeLabels = {
  '/verwalten': 'Verwalten',
  '/leistungen': 'Leistungen',
  '/casa-app': 'CASA App',
  '/insights': 'Insights',
  '/ueber-uns': 'Über uns',
  '/kontakt': 'Kontakt',
  '/impressum': 'Impressum',
  '/datenschutz': 'Datenschutzerklärung',
}
